import {ChangeDetectionStrategy, Component, inject, output, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {OverlayRef} from '@angular/cdk/overlay';
import {bzIconClose} from '@bringoz/ui/icons';
import {filter} from 'rxjs/operators';
import * as _ from 'lodash';
import {PANEL_DATA} from '../../../../commons/angular/services/panel.service';
import {EditEntryDialogData, EditEntryDialogResult} from '../../../types/timekeeping-dialog.type';
import {durationToMillis, splitDuration} from '../../utils/timekeeping-dialog.util';

type EditMode = EditEntryDialogData['editMode'];

const FIELD_LABELS: Record<EditMode, string> = {
  arrival: 'Arrival',
  departure: 'Departure',
  distance: 'Distance',
  serviceTime: 'Service Time'
};

/**
 * Panel under the edited cell for one field of a stop: arrival or departure time, service time or
 * distance. The time is shown and entered in the user's selected timezone, the one the stops grid uses.
 */
require('../timekeeping-dialogs.scss');
@Component({
  standalone: false,
  selector: 'app-timecard-edit-entry-panel',
  templateUrl: './timecard-edit-entry-panel.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TimecardEditEntryPanelComponent {

  // PanelService closes the panel on this output, so the name is part of its contract.
  readonly onClose = output<EditEntryDialogResult | null>();

  readonly data = inject<EditEntryDialogData>(PANEL_DATA);
  readonly icons = {close: bzIconClose};

  readonly editMode: EditMode = this.data.editMode || 'serviceTime';
  readonly fieldLabel = FIELD_LABELS[this.editMode];
  /** Without a value yet, the initial one carries the entry's date. */
  readonly selectedTime = signal<number>(this.editMode === 'arrival'
    ? this.data.arrival || this.data.initialArrival || Date.now()
    : this.data.departure || this.data.initialDeparture || Date.now());
  readonly hours = signal<string>(_.toString(splitDuration(this.data.serviceTime).hours));
  readonly minutes = signal<string>(_.toString(splitDuration(this.data.serviceTime).minutes));
  readonly distance = signal<string>(_.isNil(this.data.distance) ? '' : _.toString(this.data.distance));
  readonly validationError = signal<string | null>(null);

  constructor() {
    inject(OverlayRef).keydownEvents().pipe(
      filter((event) => event.key === 'Escape'),
      takeUntilDestroyed()
    ).subscribe(() => this.onCancel());
  }

  onTimeSelect(time: number) {
    this.selectedTime.set(time);
    this.validateTime();
  }

  /** Keeps minutes inside 0-59 as the user types. */
  onMinutesChange(value: string) {
    const minutes = Number(value);
    if (value === '' || !Number.isFinite(minutes)) {
      this.minutes.set(value);
      return;
    }
    this.minutes.set(_.toString(minutes < 0 ? 0 : minutes % 60));
  }

  onDistanceChange(value: string) {
    this.distance.set(value);
    this.validateDistance();
  }

  onCancel() {
    this.onClose.emit(null);
  }

  onSubmit() {
    const isValid = this.editMode === 'distance' ? this.validateDistance() : this.validateTime();
    if (!isValid) {
      return;
    }

    const {arrival, departure, serviceTime} = this.data;
    switch (this.editMode) {
      case 'arrival':
        this.onClose.emit({arrival: this.selectedTime(), departure, serviceTime});
        break;
      case 'departure':
        this.onClose.emit({arrival, departure: this.selectedTime(), serviceTime});
        break;
      case 'distance':
        this.onClose.emit({arrival, departure, serviceTime, distance: _.round(this.parseDistance(), 2)});
        break;
      default:
        this.onClose.emit({arrival, departure, serviceTime: durationToMillis(this.hours(), this.minutes())});
    }
  }

  private validateTime(): boolean {
    this.validationError.set(this.getTimeError());
    return !this.validationError();
  }

  private getTimeError(): string | null {
    const time = this.selectedTime();
    const {clockInTime, clockOutTime, arrival, departure} = this.data;
    if (this.editMode === 'arrival') {
      if (clockInTime && time < clockInTime) return 'Arrival time cannot be before clock-in time';
      if (clockOutTime && time > clockOutTime) return 'Arrival time cannot be after clock-out time';
      if (!clockOutTime && time > Date.now()) return 'ARRIVAL_TIME_CANNOT_BE_IN_THE_FUTURE';
      if (departure && time > departure) return 'Arrival time cannot be after departure time';
    } else if (this.editMode === 'departure') {
      if (clockInTime && time < clockInTime) return 'Departure time cannot be before clock-in time';
      if (clockOutTime && time > clockOutTime) return 'Departure time cannot be after clock-out time';
      if (!clockOutTime && time > Date.now()) return 'DEPARTURE_TIME_CANNOT_BE_IN_THE_FUTURE';
      if (arrival && time < arrival) return 'Departure time cannot be before arrival time';
    }
    return null;
  }

  private validateDistance(): boolean {
    const distance = this.parseDistance();
    this.validationError.set(!_.isFinite(distance) ? 'Required' : distance < 0 ? 'DISTANCE_CANNOT_BE_NEGATIVE' : null);
    return !this.validationError();
  }

  // An empty field is "no value", not zero
  private parseDistance(): number {
    return this.distance() === '' ? NaN : Number(this.distance());
  }
}
